import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

const SIZE_CLASSES = {
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Short subtitle shown under the title, explaining what this dialog does. */
  description?: ReactNode;
  children: ReactNode;
  /** Footer actions (buttons). */
  footer?: ReactNode;
  /** Dialog width. Default `md` (matches every existing modal). Use `lg`/`xl` for forms with many fields. */
  size?: keyof typeof SIZE_CLASSES;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop — a plain scrim, no backdrop-blur: animating opacity on a
              blurred layer forces a full-viewport blur re-sample every frame,
              which is the main source of "modal open" jank, especially over
              content-heavy pages (tables, charts). */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-zinc-950/55 dark:bg-zinc-950/70"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          />

          {/* Dialog content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring' as const, stiffness: 300, damping: 25 }}
            className={`relative w-full ${SIZE_CLASSES[size]} bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-white/10 shadow-soft dark:shadow-glass-dark rounded-[24px] overflow-hidden flex flex-col max-h-full`}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
          >
            <div className="flex items-start justify-between gap-4 p-6 border-b border-zinc-100 dark:border-white/5 bg-zinc-50 dark:bg-zinc-900/60 dark:backdrop-blur-md">
              <div className="flex flex-col gap-1">
                <span className="font-display font-medium text-lg text-zinc-900 dark:text-white">
                  {title}
                </span>
                {description !== undefined && (
                  <span className="text-sm font-normal text-zinc-500 dark:text-zinc-400">
                    {description}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800/50 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white transition-colors shrink-0"
                onClick={onClose}
                aria-label="Close dialog"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">{children}</div>

            {footer !== undefined && (
              <div className="p-6 border-t border-zinc-100 dark:border-white/5 bg-zinc-50/50 dark:bg-zinc-900/40 flex justify-end gap-3 rounded-b-[24px]">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
